import _ from 'lodash'
import { useEffect } from 'react'
import { DeepPartial, useFormContext } from 'react-hook-form'

import {
  Schema,
  SchemaAdUnitBidderParams,
  SchemaAdUnitBidderProps,
} from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import { ParamsKind } from 'features/globalSetup/pages/Prebid/Bidders/types'
import { AdUnitBidder } from 'features/globalSetup/types/adUnitBidder'
import { DomainBidderParameter } from 'features/globalSetup/types/domainBidder'

type Props = {
  adUnitBidderId: AdUnitBidder['id']
  kind: ParamsKind
  requiredParams: DomainBidderParameter['name'][]
}

type SchemaSlice = Pick<Schema, 'adUnitBidders'>

export const useDisableByEmptyRequiredParams = ({
  adUnitBidderId,
  kind,
  requiredParams,
}: Props): void => {
  const { setValue, watch } = useFormContext<SchemaSlice>()

  useEffect(() => {
    const { unsubscribe } = watch((schema: DeepPartial<SchemaSlice>) => {
      const adUnitBidderProps: DeepPartial<SchemaAdUnitBidderProps> | undefined =
        schema.adUnitBidders?.[adUnitBidderId]
      if (_.isEmpty(adUnitBidderProps)) return

      const adUnitBidderParams: Partial<SchemaAdUnitBidderParams> | undefined =
        adUnitBidderProps[`${kind}Params`]
      if (_.isEmpty(adUnitBidderParams)) return

      const onlyRequired: Partial<SchemaAdUnitBidderParams> = _.pickBy(
        adUnitBidderParams,
        (_, key: string) => requiredParams.includes(key),
      )
      const anyEmptyRequired: boolean = _.some(
        onlyRequired,
        (value?: null | number | string) => _.isString(value) && value.length === 0,
      )

      const currentValue: boolean | undefined = adUnitBidderProps[`${kind}DisabledByRequiredParams`]

      if (currentValue !== anyEmptyRequired) {
        setValue(
          `adUnitBidders.${adUnitBidderId}.${kind}DisabledByRequiredParams`,
          anyEmptyRequired,
        )
      }
    })

    return (): void => unsubscribe()
  }, [watch])
}
